'use client'
import { useAuth, useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import { useEffect, useState } from 'react'

import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { getNonce } from '@src/utils/nonce.utils'

import useSwellCustomerIdentification from '../../hooks/use-swell-customer-identification'
import useCustomerSearch from '../../hooks/useCustomerSearch'

import styles from './referral-yotpo-widget.module.scss'

// Extracted so we don't suppress the no-literal-string lint rule for a control-character constant.
const ELEMENT_TYPE_SCRIPT = 'script'
const SCRIPT_ID_PREFIX = 'yotpo-referral-widget-loader'

export interface ReferralYotpoWidgetProps {
  /** Yotpo loader GUID. URL: https://cdn-widgetsrepository.yotpo.com/v1/loader/{loaderGuid} */
  loaderGuid?: string
  /** Yotpo widget instance id (rendered as `data-yotpo-instance-id`). */
  widgetInstanceId?: string
}

/**
 * Yotpo referral widget (ULU-117). Identifies logged-in shoppers via `#swell-customer-identification`
 * and holds the loader + placeholder until auth/customer-search settle, so Yotpo hydrates identified.
 */
const ReferralYotpoWidget = ({ loaderGuid, widgetInstanceId }: ReferralYotpoWidgetProps) => {
  const [isWidgetReady, setIsWidgetReady] = useState(false)

  const { email } = useCurrentUser()
  const { isLoggedIn, isInitializing } = useAuth()
  const { customerId, yotpoDataToken, customerData, error: customerSearchError } = useCustomerSearch()
  const { swellRequiresToken = false } = useStatsigDynamicLocaleConfig()

  useSwellCustomerIdentification({
    email,
    customerId,
    yotpoDataToken,
    requiresToken: swellRequiresToken,
    enabled: isLoggedIn,
  })

  // Wait until we know who the shopper is (auth settled; logged-in also needs the customer search
  // settled) so identification is in the DOM before Yotpo scans — else it boots anonymously.
  const isIdentificationResolved =
    !isInitializing && (!isLoggedIn || customerData !== null || customerSearchError !== null)

  useEffect(() => {
    if (!isBrowser() || !loaderGuid || !isIdentificationResolved) return undefined

    const scriptId = `${SCRIPT_ID_PREFIX}-${loaderGuid}`
    // The loader may already be on the page from another integration (useSwellSDK's #swell-sdk) and
    // Yotpo scans only once per script execution — so re-scan via initWidgets() instead of duplicating.
    const existing = document.getElementById(scriptId) ?? document.querySelector(`script[src*="${loaderGuid}"]`)

    if (existing) {
      window.yotpoWidgetsContainer?.initWidgets?.()
      // initWidgets is synchronous; flip ready on the next tick so it has time to inject DOM.
      const t = window.setTimeout(() => {
        setIsWidgetReady(true)
      }, 0)
      return () => {
        window.clearTimeout(t)
      }
    }

    const script = document.createElement(ELEMENT_TYPE_SCRIPT)
    script.id = scriptId
    script.async = true
    script.src = `https://cdn-widgetsrepository.yotpo.com/v1/loader/${loaderGuid}`
    script.addEventListener('load', () => {
      setIsWidgetReady(true)
    })

    const nonce = getNonce()
    if (nonce) {
      script.setAttribute('nonce', nonce)
    }

    document.head.appendChild(script)
    return undefined
  }, [loaderGuid, isIdentificationResolved])

  if (!loaderGuid || !widgetInstanceId) return null

  return (
    <div className={styles.widgetContainer}>
      <div className={styles.widgetSlot}>
        {!isWidgetReady && (
          <div className={styles.skeleton} aria-busy="true" aria-live="polite" role="status">
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonBlock} />
          </div>
        )}
        {/* Held back until identification resolves — an earlier Yotpo scan would hydrate it anonymously. */}
        {isIdentificationResolved && (
          <div className="yotpo-widget-instance" data-yotpo-instance-id={widgetInstanceId} />
        )}
      </div>
    </div>
  )
}

export default ReferralYotpoWidget
